import { ChangeEvent, useEffect, useRef, useState } from 'react'
import './OtpInput.css'
import { cx } from '@/utils/cx'

export type OtpInputProps = {
  value: string
  valueLength: number
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  className?: string
  alphanumeric?: boolean
}

export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  valueLength,
  onChange,
  onFocus,
  onBlur,
  className,
  alphanumeric,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isInteracting, setIsInteracting] = useState(false)

  const sanitize = (val: string) =>
    alphanumeric
      ? val.replace(/[^a-zA-Z0-9]/g, '').slice(0, valueLength)
      : val.replace(/\D/g, '').slice(0, valueLength)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const sanitized = sanitize(raw)

    if (raw.length === value.length && sanitized.length < value.length) {
      requestAnimationFrame(() => {
        inputRef.current!.value = value
        inputRef.current?.setSelectionRange(activeIndex, activeIndex + 1)
      })
      return
    }
    onChange(sanitized)

    requestAnimationFrame(() => {
      const caret = inputRef.current?.selectionStart ?? 0
      const nextIndex = Math.min(caret, valueLength - 1)

      setActiveIndex(nextIndex)
      inputRef.current?.setSelectionRange(nextIndex, nextIndex + 1)
    })
  }

  const handleOnBlur = () => {
    onBlur?.()
    setIsInteracting(false)
  }

  const handleBoxClick = (index: number) => {
    setIsInteracting(true)

    inputRef.current?.focus()
    onFocus?.()

    requestAnimationFrame(() => {
      const parsedIndex = value.length > index ? index : value.length
      setActiveIndex(parsedIndex)
      inputRef.current?.setSelectionRange(parsedIndex, parsedIndex + 1)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isValid = alphanumeric ? /^[a-zA-Z0-9]$/.test(e.key) : /^\d$/.test(e.key)
    if (!isValid) return

    requestAnimationFrame(() => {
      const caret = inputRef.current?.selectionStart ?? 0

      const nextIndex = Math.min(caret, valueLength - 1)

      setActiveIndex(nextIndex)
      inputRef.current?.setSelectionRange(nextIndex, nextIndex + 1)
    })
  }

  useEffect(() => {
    if (value !== '') return

    setActiveIndex(0)

    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(0, 1)
    })
  }, [value])

  useEffect(() => {
    if (!('OTPCredential' in window)) return

    const controller = new AbortController()

    navigator.credentials
      .get({
        otp: { transport: ['sms'] },
        signal: controller.signal,
      })
      .then((credential) => {
        const otpCredential = credential as OTPCredential | null

        if (otpCredential?.code) {
          onChange(sanitize(otpCredential.code))
        }
      })
      .catch(() => {})

    return () => controller.abort()
  }, [onChange, valueLength])

  return (
    <div className={cx('otp-input__container', className)}>
      <div className="otp-input__boxes">
        {Array.from({ length: valueLength }).map((_, index) => (
          <div
            key={`otp-box-${index}`}
            className={cx(
              'otp-input__box',
              activeIndex === index && isInteracting && 'otp-input__box-active'
            )}
            onClick={() => handleBoxClick(index)}
          >
            {value[index] || ''}
          </div>
        ))}
      </div>

      <input
        className="otp-input__hidden-input"
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleOnBlur}
        type="text"
        inputMode={alphanumeric ? 'text' : 'numeric'}
        pattern={alphanumeric ? `[a-zA-Z0-9]{${valueLength}}` : `\\d{${valueLength}}`}
        autoComplete="one-time-code"
        aria-label="One-time password"
      />
    </div>
  )
}
